import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { questionCmsAPI } from '../lib/api';
import {
  ArrowLeft,
  Search,
  Edit3,
  Save,
  X,
  RotateCcw,
  FileText,
  Loader2,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Shield,
  ClipboardList,
  Download,
  Upload,
  CheckCircle,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const ASSESSMENT_TYPES = [
  { value: 'HIPAA', label: 'HIPAA', description: '158 Questions' },
  { value: 'CMMC', label: 'CMMC Level 2', description: '125 Questions' },
  { value: 'SOC2', label: 'SOC 2', description: 'Service Organization Controls' },
  { value: 'SOC2_TYPE2', label: 'SOC 2 Type 2', description: 'Extended Controls' },
  { value: 'FINRA', label: 'FINRA', description: 'Financial Industry Regulatory' },
  { value: 'NIST_CSF', label: 'NIST CSF', description: 'Cybersecurity Framework' },
];

const parseCSVRow = (row) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === '"') {
      if (inQuotes && i + 1 < row.length && row[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
};

const parseCSVFile = (text) => {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const rows = lines.slice(1);
  return rows.map(row => {
    const cols = parseCSVRow(row);
    const mustStr = (cols[4] || '').trim();
    const evidenceStr = (cols[5] || '').trim();
    const priorityStr = (cols[8] || '').trim();
    return {
      questionId: (cols[0] || '').trim(),
      questionText: (cols[3] || '').trim() || null,
      mustStatements: mustStr ? mustStr.split(' | ').map(s => s.trim()).filter(Boolean) : null,
      evidenceRequired: evidenceStr ? evidenceStr.split(' | ').map(s => s.trim()).filter(Boolean) : null,
      plainLanguage: (cols[6] || '').trim() || null,
      assessmentGuidance: (cols[7] || '').trim() || null,
      priorityNumber: priorityStr ? parseInt(priorityStr, 10) : null,
    };
  }).filter(q => q.questionId);
};

const parseJSONFile = (text) => {
  const data = JSON.parse(text);
  const questions = data.questions || data;
  if (!Array.isArray(questions)) return [];
  return questions.map(q => ({
    questionId: q.questionId,
    questionText: q.questionText || null,
    mustStatements: q.mustStatements?.length ? q.mustStatements : null,
    evidenceRequired: q.evidenceRequired?.length ? q.evidenceRequired : null,
    plainLanguage: q.plainLanguage || null,
    assessmentGuidance: q.assessmentGuidance || null,
    priorityNumber: q.priorityNumber ?? null,
  })).filter(q => q.questionId);
};

const QuestionCMS = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [selectedType, setSelectedType] = useState('HIPAA');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [filterModified, setFilterModified] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Import/Export state
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importApplying, setImportApplying] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importFileName, setImportFileName] = useState('');

  // Evidence state
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [evidenceCounts, setEvidenceCounts] = useState({});
  const evidenceInputRef = useRef(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/');
      return;
    }
    fetchQuestions();
  }, [selectedType, user]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const [qRes, evRes] = await Promise.all([
        questionCmsAPI.getQuestions(selectedType),
        questionCmsAPI.getEvidenceSummary(selectedType),
      ]);
      setQuestions(qRes.data.questions || []);
      setEvidenceCounts(evRes.data.evidenceCounts || {});
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  // === Export ===
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await questionCmsAPI.exportQuestions(selectedType, format);
      const blob = new Blob([response.data], {
        type: format === 'json' ? 'application/json' : 'text/csv'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedType}_questions.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // === Import ===
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    setImportPreview(null);
    setImportResult(null);
    setImportLoading(true);

    try {
      const text = await file.text();
      let parsed;
      if (file.name.endsWith('.json')) {
        parsed = parseJSONFile(text);
      } else {
        parsed = parseCSVFile(text);
      }

      if (!parsed.length) {
        alert('No valid questions found in the file.');
        setImportLoading(false);
        return;
      }

      setImportData(parsed);
      // Get preview from backend
      const preview = await questionCmsAPI.previewImport(selectedType, { questions: parsed });
      setImportPreview(preview.data);
    } catch (error) {
      console.error('Failed to parse file:', error);
      alert(`Failed to parse file: ${error.message}`);
    } finally {
      setImportLoading(false);
    }
    // Reset file input so re-selecting the same file works
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleApplyImport = async () => {
    if (!importData) return;
    setImportApplying(true);
    try {
      const result = await questionCmsAPI.applyImport(selectedType, { questions: importData });
      setImportResult(result.data);
      fetchQuestions();
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Please try again.');
    } finally {
      setImportApplying(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportData(null);
    setImportPreview(null);
    setImportResult(null);
    setImportFileName('');
  };

  // === Question editing ===
  const handleEditQuestion = async (question) => {
    setEditingQuestion(question.questionId);
    setEditForm({
      questionText: question.questionText || '',
      mustStatements: question.mustStatements || [],
      evidenceRequired: question.evidenceRequired || [],
      plainLanguage: question.plainLanguage || '',
      assessmentGuidance: question.assessmentGuidance || '',
      priorityNumber: question.priorityNumber ?? ''
    });
    // Load evidence files for this question
    try {
      const res = await questionCmsAPI.listEvidence(selectedType, question.questionId);
      setEvidenceFiles(res.data.evidence || []);
    } catch {
      setEvidenceFiles([]);
    }
  };

  const handleEvidenceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingQuestion) return;
    setEvidenceUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await questionCmsAPI.uploadEvidence(selectedType, editingQuestion, formData);
      const res = await questionCmsAPI.listEvidence(selectedType, editingQuestion);
      setEvidenceFiles(res.data.evidence || []);
      // Update counts
      setEvidenceCounts(prev => ({ ...prev, [editingQuestion]: (prev[editingQuestion] || 0) + 1 }));
    } catch (error) {
      console.error('Evidence upload failed:', error);
      alert('Upload failed. Check file size (max 10 MB) and type.');
    } finally {
      setEvidenceUploading(false);
      if (evidenceInputRef.current) evidenceInputRef.current.value = '';
    }
  };

  const handleEvidenceDelete = async (evidenceId) => {
    if (!window.confirm('Delete this evidence file?')) return;
    try {
      await questionCmsAPI.deleteEvidence(selectedType, editingQuestion, evidenceId);
      setEvidenceFiles(prev => prev.filter(e => e.id !== evidenceId));
      setEvidenceCounts(prev => ({ ...prev, [editingQuestion]: Math.max(0, (prev[editingQuestion] || 1) - 1) }));
    } catch (error) {
      console.error('Evidence delete failed:', error);
    }
  };

  const handleEvidenceDownload = async (ev) => {
    try {
      const res = await questionCmsAPI.downloadEvidence(selectedType, editingQuestion, ev.id);
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ev.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSaveQuestion = async () => {
    setSaving(true);
    try {
      const updateData = {};
      if (editForm.questionText) updateData.questionText = editForm.questionText;
      if (editForm.mustStatements?.length > 0) updateData.mustStatements = editForm.mustStatements;
      if (editForm.evidenceRequired?.length > 0) updateData.evidenceRequired = editForm.evidenceRequired;
      if (editForm.plainLanguage) updateData.plainLanguage = editForm.plainLanguage;
      if (editForm.assessmentGuidance) updateData.assessmentGuidance = editForm.assessmentGuidance;
      if (editForm.priorityNumber !== '' && editForm.priorityNumber !== null && editForm.priorityNumber !== undefined) {
        updateData.priorityNumber = parseInt(editForm.priorityNumber, 10);
      }
      await questionCmsAPI.updateQuestion(selectedType, editingQuestion, updateData);
      setEditingQuestion(null);
      fetchQuestions();
    } catch (error) {
      console.error('Failed to save question:', error);
      alert('Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleResetQuestion = async (questionId) => {
    if (!window.confirm('Reset this question to its default values? This will remove all custom edits.')) return;
    try {
      await questionCmsAPI.resetQuestion(selectedType, questionId);
      fetchQuestions();
    } catch (error) {
      console.error('Failed to reset question:', error);
    }
  };

  const handleAddMustStatement = () => {
    setEditForm({ ...editForm, mustStatements: [...(editForm.mustStatements || []), ''] });
  };
  const handleRemoveMustStatement = (index) => {
    const arr = [...editForm.mustStatements];
    arr.splice(index, 1);
    setEditForm({ ...editForm, mustStatements: arr });
  };
  const handleMustStatementChange = (index, value) => {
    const arr = [...editForm.mustStatements];
    arr[index] = value;
    setEditForm({ ...editForm, mustStatements: arr });
  };
  const handleAddEvidenceItem = () => {
    setEditForm({ ...editForm, evidenceRequired: [...(editForm.evidenceRequired || []), ''] });
  };
  const handleRemoveEvidenceItem = (index) => {
    const arr = [...editForm.evidenceRequired];
    arr.splice(index, 1);
    setEditForm({ ...editForm, evidenceRequired: arr });
  };
  const handleEvidenceItemChange = (index, value) => {
    const arr = [...editForm.evidenceRequired];
    arr[index] = value;
    setEditForm({ ...editForm, evidenceRequired: arr });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Filter questions
  const filteredQuestions = questions.filter(q => {
    const matchesSearch = !searchQuery ||
      q.questionId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.questionText.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.category?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesModified = !filterModified || q.hasOverride;
    return matchesSearch && matchesModified;
  });

  // Group questions by category
  const groupedQuestions = filteredQuestions.reduce((acc, q) => {
    const category = q.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(q);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">Question Content Manager</h1>
                <p className="text-slate-400 text-sm">Edit question text and compliance requirements</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-400 text-sm">
                {filteredQuestions.length} questions
                {filterModified && ` (${questions.filter(q => q.hasOverride).length} modified)`}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Controls */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Assessment Type Selector */}
            <div className="flex-shrink-0">
              <label className="block text-slate-400 text-xs mb-1">Assessment Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="assessment-type-select"
              >
                {ASSESSMENT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="flex-1 min-w-[250px]">
              <label className="block text-slate-400 text-xs mb-1">Search Questions</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by ID, text, or category..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Filter Modified */}
            <div className="flex items-center gap-2 pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterModified}
                  onChange={(e) => setFilterModified(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500"
                />
                <span className="text-slate-300 text-sm">Modified Only</span>
              </label>
            </div>

            {/* Import/Export Buttons */}
            <div className="flex items-center gap-2 pt-4 ml-auto">
              <button
                onClick={() => handleExport('csv')}
                disabled={exporting || loading}
                className="px-3 py-2 bg-emerald-600/20 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-600/30 rounded-lg text-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                data-testid="export-csv-btn"
              >
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                disabled={exporting || loading}
                className="px-3 py-2 bg-blue-600/20 border border-blue-500/40 text-blue-400 hover:bg-blue-600/30 rounded-lg text-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                data-testid="export-json-btn"
              >
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                JSON
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                disabled={loading}
                className="px-3 py-2 bg-violet-600/20 border border-violet-500/40 text-violet-400 hover:bg-violet-600/30 rounded-lg text-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                data-testid="import-btn"
              >
                <Upload className="w-3.5 h-3.5" />
                Import
              </button>
            </div>
          </div>
        </div>

        {/* Questions List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedQuestions).map(([category, categoryQuestions]) => (
              <div key={category} className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-slate-700/50 hover:bg-slate-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {expandedCategories[category] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                    <span className="font-medium text-white">{category}</span>
                    <span className="text-slate-400 text-sm">({categoryQuestions.length} questions)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {categoryQuestions.filter(q => q.hasOverride).length > 0 && (
                      <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded-full">
                        {categoryQuestions.filter(q => q.hasOverride).length} modified
                      </span>
                    )}
                  </div>
                </button>

                {expandedCategories[category] && (
                  <div className="divide-y divide-slate-700">
                    {categoryQuestions.map(question => (
                      <div key={question.questionId} className="p-4">
                        {editingQuestion === question.questionId ? (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <span className="text-violet-400 font-mono text-sm">{question.questionId}</span>
                              <div className="flex items-center gap-2">
                                <button onClick={() => setEditingQuestion(null)} className="px-3 py-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
                                  <X className="w-4 h-4" />
                                </button>
                                <button onClick={handleSaveQuestion} disabled={saving} className="px-4 py-1.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="save-question-btn">
                                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                  Save
                                </button>
                              </div>
                            </div>
                            <div>
                              <label className="block text-slate-300 text-sm mb-1">Question Text</label>
                              <textarea value={editForm.questionText} onChange={(e) => setEditForm({ ...editForm, questionText: e.target.value })} rows={3} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500" data-testid="edit-question-text" />
                            </div>
                            {selectedType === 'NIST_CSF' && (
                              <div>
                                <label className="block text-slate-300 text-sm mb-1 flex items-center gap-1.5">
                                  <Shield className="w-3.5 h-3.5 text-cyan-400" />
                                  CSET Priority Number
                                </label>
                                <input type="number" min="1" max="106" value={editForm.priorityNumber} onChange={(e) => setEditForm({ ...editForm, priorityNumber: e.target.value })} className="w-32 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" placeholder="1-106" data-testid="edit-priority-number" />
                                <span className="ml-2 text-slate-500 text-xs">Lower number = higher priority</span>
                              </div>
                            )}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-slate-300 text-sm">MUST Statements</label>
                                <button onClick={handleAddMustStatement} className="px-2 py-1 text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"><Plus className="w-3 h-3" />Add Statement</button>
                              </div>
                              <div className="space-y-2">
                                {(editForm.mustStatements || []).map((statement, idx) => (
                                  <div key={idx} className="flex items-start gap-2">
                                    <span className="text-amber-400 mt-2">•</span>
                                    <input type="text" value={statement} onChange={(e) => handleMustStatementChange(idx, e.target.value)} className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="Enter MUST statement..." />
                                    <button onClick={() => handleRemoveMustStatement(idx)} className="p-2 text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                  </div>
                                ))}
                                {(!editForm.mustStatements || editForm.mustStatements.length === 0) && <p className="text-slate-500 text-sm italic">No MUST statements</p>}
                              </div>
                            </div>
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-slate-300 text-sm flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5 text-cyan-400" />Evidence Requirements</label>
                                <button onClick={handleAddEvidenceItem} className="px-2 py-1 text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-testid="add-evidence-btn"><Plus className="w-3 h-3" />Add Evidence</button>
                              </div>
                              <div className="space-y-2">
                                {(editForm.evidenceRequired || []).map((item, idx) => (
                                  <div key={idx} className="flex items-start gap-2">
                                    <span className="text-cyan-400 mt-2 text-sm">{idx + 1}.</span>
                                    <input type="text" value={item} onChange={(e) => handleEvidenceItemChange(idx, e.target.value)} className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" placeholder="Enter evidence requirement..." data-testid={`edit-evidence-item-${idx}`} />
                                    <button onClick={() => handleRemoveEvidenceItem(idx)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`remove-evidence-item-${idx}`}><Trash2 className="w-4 h-4" /></button>
                                  </div>
                                ))}
                                {(!editForm.evidenceRequired || editForm.evidenceRequired.length === 0) && <p className="text-slate-500 text-sm italic">No evidence requirements</p>}
                              </div>
                            </div>
                            <div>
                              <label className="block text-slate-300 text-sm mb-1">Plain Language Explanation</label>
                              <textarea value={editForm.plainLanguage || ''} onChange={(e) => setEditForm({ ...editForm, plainLanguage: e.target.value })} rows={2} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="Enter plain language explanation..." />
                            </div>
                            {/* Evidence File Uploads */}
                            <div className="border-t border-slate-700 pt-4">
                              <div className="flex items-center justify-between mb-3">
                                <label className="text-slate-300 text-sm flex items-center gap-1.5">
                                  <Upload className="w-3.5 h-3.5 text-emerald-400" />
                                  Reference Evidence Files
                                </label>
                                <div>
                                  <input ref={evidenceInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.txt,.csv,.md" onChange={handleEvidenceUpload} className="hidden" data-testid="evidence-file-input" />
                                  <button
                                    onClick={() => evidenceInputRef.current?.click()}
                                    disabled={evidenceUploading}
                                    className="px-2 py-1 text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 disabled:opacity-50"
                                    data-testid="upload-evidence-btn"
                                  >
                                    {evidenceUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                    Upload File
                                  </button>
                                </div>
                              </div>
                              {evidenceFiles.length > 0 ? (
                                <div className="space-y-2">
                                  {evidenceFiles.map(ev => (
                                    <div key={ev.id} className="flex items-center justify-between px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg group">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <FileText className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                        <span className="text-white text-sm truncate">{ev.fileName}</span>
                                        <span className="text-slate-500 text-xs flex-shrink-0">{formatFileSize(ev.fileSize)}</span>
                                      </div>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        <button onClick={() => handleEvidenceDownload(ev)} className="p-1.5 text-slate-400 hover:text-emerald-400 rounded transition-colors" title="Download" data-testid={`download-evidence-${ev.id}`}>
                                          <Download className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => handleEvidenceDelete(ev.id)} className="p-1.5 text-slate-400 hover:text-red-400 rounded transition-colors" title="Delete" data-testid={`delete-evidence-${ev.id}`}>
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-slate-500 text-sm italic">No reference files uploaded. Upload policies, templates, or guidance documents.</p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-violet-400 font-mono text-sm">{question.questionId}</span>
                                {question.hasOverride && (
                                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1"><Edit3 className="w-3 h-3" />Modified</span>
                                )}
                                {question.priorityNumber != null && (
                                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${question.priorityNumber <= 20 ? 'bg-red-500/20 text-red-400' : question.priorityNumber <= 50 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'}`}>P{question.priorityNumber}</span>
                                )}
                                {evidenceCounts[question.questionId] > 0 && (
                                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full flex items-center gap-1" data-testid={`evidence-count-${question.questionId}`}>
                                    <FileText className="w-3 h-3" />
                                    {evidenceCounts[question.questionId]} file{evidenceCounts[question.questionId] > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              <p className="text-white text-sm mb-2">{question.questionText}</p>
                              {question.mustStatements?.length > 0 && (
                                <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                  <span className="text-amber-400 text-xs font-medium">MUST Statements:</span>
                                  <ul className="mt-1 space-y-1">
                                    {question.mustStatements.slice(0, 2).map((s, idx) => (
                                      <li key={idx} className="text-slate-300 text-xs flex items-start gap-1"><span className="text-amber-400">•</span><span className="line-clamp-1">{s}</span></li>
                                    ))}
                                    {question.mustStatements.length > 2 && <li className="text-slate-400 text-xs">+{question.mustStatements.length - 2} more...</li>}
                                  </ul>
                                </div>
                              )}
                              {question.evidenceRequired?.length > 0 && (
                                <div className="mt-2 p-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                                  <span className="text-cyan-400 text-xs font-medium flex items-center gap-1"><ClipboardList className="w-3 h-3" />Evidence Requirements ({question.evidenceRequired.length}):</span>
                                  <ul className="mt-1 space-y-1">
                                    {question.evidenceRequired.slice(0, 2).map((e, idx) => (
                                      <li key={idx} className="text-slate-300 text-xs flex items-start gap-1"><span className="text-cyan-400">{idx + 1}.</span><span className="line-clamp-1">{e}</span></li>
                                    ))}
                                    {question.evidenceRequired.length > 2 && <li className="text-slate-400 text-xs">+{question.evidenceRequired.length - 2} more...</li>}
                                  </ul>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button onClick={() => handleEditQuestion(question)} className="p-2 text-slate-400 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-colors" title="Edit question" data-testid={`edit-${question.questionId}`}>
                                <Edit3 className="w-4 h-4" />
                              </button>
                              {question.hasOverride && (
                                <button onClick={() => handleResetQuestion(question.questionId)} className="p-2 text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors" title="Reset to default">
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {filteredQuestions.length === 0 && !loading && (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No questions found matching your criteria</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="import-modal">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Upload className="w-5 h-5 text-violet-400" />
                  Import Questions
                </h2>
                <p className="text-slate-400 text-sm mt-0.5">
                  Import from CSV or JSON for {ASSESSMENT_TYPES.find(t => t.value === selectedType)?.label}
                </p>
              </div>
              <button onClick={closeImportModal} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Step 1: File Upload */}
              {!importResult && (
                <div className="mb-6">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.json"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importLoading}
                    className="w-full py-8 border-2 border-dashed border-slate-600 rounded-xl hover:border-violet-500/50 hover:bg-violet-500/5 transition-all flex flex-col items-center gap-2 text-slate-400 hover:text-violet-400"
                    data-testid="import-file-select-btn"
                  >
                    {importLoading ? (
                      <Loader2 className="w-8 h-8 animate-spin" />
                    ) : (
                      <Upload className="w-8 h-8" />
                    )}
                    <span className="text-sm font-medium">
                      {importLoading ? 'Analyzing file...' : importFileName ? `Selected: ${importFileName}` : 'Click to select CSV or JSON file'}
                    </span>
                    <span className="text-xs text-slate-500">Supports .csv and .json formats exported from this CMS</span>
                  </button>
                </div>
              )}

              {/* Step 2: Preview */}
              {importPreview && !importResult && (
                <div className="space-y-4">
                  <h3 className="text-white font-medium text-sm">Import Preview</h3>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-white">{importPreview.totalImported}</div>
                      <div className="text-xs text-slate-400">Total Rows</div>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-emerald-400">{importPreview.newOverrides}</div>
                      <div className="text-xs text-emerald-400/70">New Overrides</div>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-amber-400">{importPreview.modified}</div>
                      <div className="text-xs text-amber-400/70">Will Modify</div>
                    </div>
                    <div className="bg-slate-500/10 border border-slate-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-slate-400">{importPreview.unchanged}</div>
                      <div className="text-xs text-slate-400/70">Unchanged</div>
                    </div>
                  </div>

                  {/* Invalid entries warning */}
                  {importPreview.invalid > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        {importPreview.invalid} Invalid Entries (will be skipped)
                      </div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {importPreview.details.invalid.map((inv, idx) => (
                          <div key={idx} className="text-xs text-red-300/70">
                            <span className="font-mono">{inv.questionId}</span> — {inv.reason}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New overrides detail */}
                  {importPreview.details.newOverrides.length > 0 && (
                    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
                      <div className="text-emerald-400 text-sm font-medium mb-2">New Overrides</div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {importPreview.details.newOverrides.map((item, idx) => (
                          <div key={idx} className="text-xs text-slate-300 flex items-center gap-2">
                            <span className="font-mono text-emerald-400">{item.questionId}</span>
                            <span className="text-slate-500">changes:</span>
                            {item.changedFields.map(f => (
                              <span key={f} className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-[10px]">{f}</span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Modified detail */}
                  {importPreview.details.modified.length > 0 && (
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                      <div className="text-amber-400 text-sm font-medium mb-2">Will Modify Existing Overrides</div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {importPreview.details.modified.map((item, idx) => (
                          <div key={idx} className="text-xs text-slate-300 flex items-center gap-2">
                            <span className="font-mono text-amber-400">{item.questionId}</span>
                            <span className="text-slate-500">changes:</span>
                            {item.changedFields.map(f => (
                              <span key={f} className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px]">{f}</span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {importPreview.newOverrides === 0 && importPreview.modified === 0 && importPreview.invalid === 0 && (
                    <div className="bg-slate-700/30 rounded-lg p-4 text-center text-slate-400 text-sm">
                      <CheckCircle className="w-6 h-6 mx-auto mb-2 text-slate-500" />
                      All questions are identical to current data. Nothing to import.
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Result */}
              {importResult && (
                <div className="text-center py-6">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                  <h3 className="text-white font-semibold text-lg mb-1">Import Complete</h3>
                  <p className="text-slate-400 text-sm mb-4">{importResult.message}</p>
                  <div className="flex items-center justify-center gap-6">
                    <div>
                      <div className="text-2xl font-bold text-emerald-400">{importResult.applied}</div>
                      <div className="text-xs text-slate-400">Applied</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-400">{importResult.skipped}</div>
                      <div className="text-xs text-slate-400">Skipped</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-end gap-3">
              {importResult ? (
                <button onClick={closeImportModal} className="px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm" data-testid="import-done-btn">
                  Done
                </button>
              ) : (
                <>
                  <button onClick={closeImportModal} className="px-4 py-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg text-sm">
                    Cancel
                  </button>
                  {importPreview && (importPreview.newOverrides > 0 || importPreview.modified > 0) && (
                    <button
                      onClick={handleApplyImport}
                      disabled={importApplying}
                      className="px-5 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                      data-testid="import-apply-btn"
                    >
                      {importApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Apply {importPreview.newOverrides + importPreview.modified} Changes
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionCMS;
